import { memo, type PropsWithChildren } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SortOrder } from "consts/request-query";
import type { IncrementTaxes as MerchantPayoutIncrementTaxes } from "features/merchantPayouts/types";
import { Collection, Resource } from "ui/support/Resource";
import Card from "ui/atoms/Card";
import type { Sortable, WithPrepopulatedFormValues } from "./types";
import useFeesAndCommisions from "../useFeesAndCommisions";
import Fees, { type FeesProps } from "./Fees";

type Props = Sortable<
    WithPrepopulatedFormValues<{
        readonly incrementTaxes: Array<MerchantPayoutIncrementTaxes>;
    }>
>;

const FeesByTaxNameGroup = ({
    taxName,
    incrementTaxes,
    sortOrder
}: PropsWithChildren<Props>) => {
    const { groupIncrementTaxesBy } = useFeesAndCommisions();

    const fees = Object.entries<MerchantPayoutIncrementTaxes[]>(groupIncrementTaxesBy({
        groupByKey: 'refColFromMinSum',
        incrementTaxes
    }))
        .reduce((acc, [refColFromMinSum, incrementTaxes]) => {
            const refColFromMinSumNumber = Number(refColFromMinSum);
            const prev = acc.at(-1);

            if (prev) {
                prev.end = refColFromMinSumNumber - 1;
            }

            return [
                ...acc,
                {
                    start: refColFromMinSumNumber,
                    end: Number.POSITIVE_INFINITY,
                    incrementTaxes: incrementTaxes.sort((a, b) => (
                        Object.is(sortOrder, SortOrder.Asc)
                            ? a.refColFromMinSum - b.refColFromMinSum
                            : b.refColFromMinSum - a.refColFromMinSum
                    ))
                }
            ];
        }, Array.of<FeesProps>());

    return (
        <Resource
            isEmpty={!fees.length}
            emptySlot={<></>}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 2
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    {taxName}
                </Typography>
                <Collection
                    component={Card}
                    sx={{
                        p: 2,
                        display: 'grid',
                        gap: 2
                    }}
                    collection={fees}
                    renderResource={(props, index) => (
                        <Fees
                            {...props}
                            key={String(index)}
                        />
                    )}
                />
            </Box>
        </Resource>
    );
};

export default memo(FeesByTaxNameGroup);
